import { useState } from 'react';

import Button from '@/components/shared/Button/Button';
import { TextField } from '@/components/shared/TextField/TextField';
import { ROUTES } from '@/lib/routes';
import { useContentViewStore } from '@/lib/store/contentViewStore';
import { ExploreBlock, ExploreBlockType, JourneyCheckInHub } from '@addmile/backend-client';

import { blockInfo } from '../../../blockInfo';
import { BlockDraft, JourneyDraft, UpdateDraft } from '../../../draft';
import { checkInContentSchema } from '../../../jsonSchemas';
import { DeleteTarget, Selection } from '../../../selection';
import BlockPickerModal from '../BlockPickerModal/BlockPickerModal';
import Field from '../Form/Field';
import Section from '../Form/Section';
import JsonField from '../JsonField/JsonField';
import PanelHeader from '../PanelHeader/PanelHeader';
import { toBlockContent } from './toBlockContent';

interface BlockPanelProps {
  draft: JourneyDraft;
  mileIndex: number;
  stepIndex: number;
  blockIndex: number;
  update: UpdateDraft;
  locked: boolean;
  onSelect: (selection: Selection) => void;
  onDelete: (target: DeleteTarget) => void;
}

// Explore's editor opens from its persisted view state, so set it and open Explore in a new tab,
// leaving this journey's unsaved changes alone.
const openInExplore = (slug: string) => {
  useContentViewStore.setState({
    viewMode: 'editor',
    editingSlug: slug,
    draftType: undefined,
    editorReturnView: 'list',
  });
  window.open(ROUTES.CONTENT_EXPLORE, '_blank');
};

const BlockPanel = ({
  draft,
  mileIndex,
  stepIndex,
  blockIndex,
  update,
  locked,
  onSelect,
  onDelete,
}: BlockPanelProps) => {
  const [pickerType, setPickerType] = useState<ExploreBlockType>();
  const mile = draft.miles[mileIndex];
  const step = mile.steps[stepIndex];
  const block = step.blocks[blockIndex];
  const { content, checkIn } = block;
  const { icon, typeLabel, title } = blockInfo(block);

  const editBlock = (recipe: (block: BlockDraft) => void) =>
    update((d) => recipe(d.miles[mileIndex].steps[stepIndex].blocks[blockIndex]));

  const editCheckIn = (recipe: (checkIn: JourneyCheckInHub) => void) =>
    editBlock((b) => b.checkIn && recipe(b.checkIn));

  const selectBlock = (index: number) =>
    onSelect({
      kind: 'block',
      mileKey: mile.key,
      stepKey: step.key,
      blockKey: step.blocks[index].key,
    });

  const deleteBlock = () =>
    onDelete({ kind: 'block', mileKey: mile.key, stepKey: step.key, blockKey: block.key });

  const replaceBlock = (picked: ExploreBlock) => {
    editBlock((b) => void (b.content = toBlockContent(picked)));
    setPickerType(undefined);
  };

  return (
    <div className="grid gap-6">
      <BlockPickerModal
        type={pickerType}
        onPick={replaceBlock}
        onClose={() => setPickerType(undefined)}
      />

      <PanelHeader
        eyebrow={`Mile ${mileIndex + 1} · Step ${mileIndex + 1}.${stepIndex + 1} · Block ${blockIndex + 1} of ${step.blocks.length}`}
        title={title}
        icon={icon && <img src={icon} alt="" className="w-7 h-7" />}
        aside={<span className="text-neutral-50">{typeLabel}</span>}
        deleteLabel="Delete block"
        onDelete={locked ? undefined : deleteBlock}
      />

      {content && (
        <Section title="Explore block">
          <div className="flex items-center gap-4">
            {content.imageUrl ? (
              <img
                src={content.imageUrl}
                alt=""
                className="w-16 h-16 rounded-xl object-cover bg-white-100"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white-100" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-neutral-90">{content.title}</p>
              <p className="text-sm text-neutral-50">{content.slug}</p>
              {content.duration && (
                <p className="text-sm text-neutral-50">
                  {content.duration.value} {content.duration.unit}
                </p>
              )}
            </div>
          </div>
          <p className="text-sm text-neutral-60">
            The block’s content lives in Explore; edits there show up in every journey using it.
          </p>
          <div className="flex gap-3">
            <Button
              label="Open in Explore"
              variant="transparent"
              size="s"
              className="!w-fit px-4 bg-white-100"
              onClick={() => openInExplore(content.slug)}
            />
            {!locked && (
              <Button
                label="Replace block"
                variant="transparent"
                size="s"
                className="!w-fit px-4 bg-white-100"
                onClick={() => setPickerType(content.type)}
              />
            )}
          </div>
        </Section>
      )}

      {checkIn && (
        <>
          <Section title="General Info">
            <Field label="Title" hint="Listed on the step’s cover">
              <TextField
                className="!w-full"
                inputClassName="!font-normal"
                value={checkIn.title}
                placeholder="e.g. Feeling Secure"
                onChange={(e) => editCheckIn((c) => void (c.title = e.target.value))}
              />
            </Field>
            <Field label="Description">
              <TextField
                isTextArea
                rows={3}
                className="!w-full"
                inputClassName="!font-normal"
                value={checkIn.description}
                placeholder="e.g. One question — your starting point for the mile"
                onChange={(e) => editCheckIn((c) => void (c.description = e.target.value))}
              />
            </Field>
          </Section>
          <div className="grid gap-3">
            <h3 className="text-lg font-semibold text-neutral-90">Content (JSON)</h3>
            <JsonField
              key={block.key}
              value={{ question: checkIn.question, scale: checkIn.scale, after: checkIn.after }}
              schema={checkInContentSchema}
              onChange={(text) => editCheckIn((c) => void Object.assign(c, text))}
            />
          </div>
        </>
      )}

      <div className="flex justify-between border-t border-neutral-10 pt-4">
        <Button
          label="‹ Previous block"
          variant="transparent"
          size="s"
          className="!w-fit px-4"
          disabled={blockIndex === 0}
          onClick={() => selectBlock(blockIndex - 1)}
        />
        <Button
          label="Next block ›"
          variant="transparent"
          size="s"
          className="!w-fit px-4"
          disabled={blockIndex === step.blocks.length - 1}
          onClick={() => selectBlock(blockIndex + 1)}
        />
      </div>
    </div>
  );
};

export default BlockPanel;
